import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { SimpleAuthProvider } from './features/auth/SimpleAuthContext'
import { AnonymousUserProvider } from './contexts/AnonymousUserContext'
import { ToastProvider } from './context/ToastContext'
import Layout from './components/layout/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import { AdminRoute, ModeratorRoute } from './components/admin/ProtectedRoute'
import LoadingSpinner, { CardSkeleton } from './components/common/LoadingSpinner'
import { GlassSurface } from './components/common/GlassSurface'
import ErrorBoundary from './components/common/ErrorBoundary'
import { scheduleCleanup } from './services/chatService'

// Componentes de carga mejorados
const PageLoader = ({ message = 'Cargando página...', showSkeleton = false }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-white to-orange-50">
    <div className="text-center space-y-6 p-8">
      <LoadingSpinner size="xl" color="#3BC6C6" />
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-gray-800 animate-pulse">
          {message}
        </h2>
        <p className="text-sm text-gray-500">Esto solo tomará un momento...</p>
      </div>
      {showSkeleton && (
        <div className="mt-8 space-y-4 max-w-md mx-auto">
          <CardSkeleton />
        </div>
      )}
    </div>
  </div>
)

const MinimalLoader = () => (
  <div className="flex items-center justify-center py-12">
    <LoadingSpinner size="medium" color="#3BC6C6" />
  </div>
)

const AuthLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-100 via-white to-orange-100">
    <div className="text-center space-y-4 p-8">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center">
        <span className="text-white text-2xl font-bold">W</span>
      </div>
      <LoadingSpinner size="large" color="#3BC6C6" />
      <p className="text-gray-600">Conectando con Wyra...</p>
    </div>
  </div>
)

// Lazy loading - HomePage ya tiene lazy load interno de Particles
const HomePage = lazy(() => import('./pages/HomePage'))

// Páginas de autenticación
const NeoPixelLoginPage = lazy(() => import('./pages/auth/NeoPixelLoginPage'))
const NeoPixelRegisterPage = lazy(() => import('./pages/auth/NeoPixelRegisterPage'))

// Lazy loading de páginas secundarias con agrupación inteligente
const ComunidadPage = lazy(() => import('./pages/comunidad/ComunidadPage.jsx'))
const ComunidadGruposPage = lazy(() => import('./pages/comunidad/ComunidadGruposPage.jsx'))
const ComunidadEventosPage = lazy(() => import('./pages/comunidad/ComunidadEventosPage.jsx'))
const ComunidadChatPage = lazy(() => import('./pages/comunidad/ComunidadChatPage.jsx'))
const ComunidadSpotsPage = lazy(() => import('./pages/comunidad/ComunidadSpotsPage.jsx'))
const AcademiesPage = lazy(() => import('./pages/academies/AcademiesPage'))
const NeoAcademiesPage = lazy(() => import('./pages/academies/NeoAcademiesPage'))
const AcademyDetailPage = lazy(() => import('./pages/academies/AcademyDetailPage'))
const TermsPage = lazy(() => import('./pages/legal/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'))
const ContactPage = lazy(() => import('./pages/contact/ContactPage'))

// Páginas de grupos - lazy loading estándar
const SimpleGroupsPage = lazy(() => import('./pages/simple/SimpleGroupsPage'))
const GroupsPage = lazy(() => import('./pages/groups/GroupsPage'))

const GroupDetailPage = lazy(() => import('./pages/groups/GroupDetailPage'))
const ManageGroupPage = lazy(() => import('./pages/groups/ManageGroupPage'))
const PredefinedGroupsPage = lazy(() => import('./pages/groups/PredefinedGroupsPage'))
const NeoGroupsPage = lazy(() => import('./pages/simple/NeoGroupsPage'))

// Página de edición de grupos
const EditGroupPageWrapper = lazy(() => import('./pages/groups/EditGroupPage'))

// Páginas sociales - relacionadas
// SimpleChatPage eliminado - ahora se usa ComunidadChatPage
const SimpleProfilePage = lazy(() => import('./pages/simple/NeoProfilePage'))

// Páginas de configuración y administración
const NotificationSettingsPage = lazy(() => import('./pages/settings/NotificationSettingsPage'))
const AdminPage = lazy(() => import('./pages/admin/AdminPage'))
const ModerationPage = lazy(() => import('./pages/admin/ModerationPage'))
const TempAdminPage = lazy(() => import('./pages/admin/TempAdminPage'))
const DeleteAllEventsPage = lazy(() => import('./pages/admin/util/DeleteAllEventsPage'))
const MaintenancePage = lazy(() => import('./pages/maintenance/MaintenancePage'))

// Páginas comerciales
const SimpleShopsPage = lazy(() => import('./pages/simple/SimpleShopsPage'))
const ShopsPage = lazy(() => import('./pages/shops/ShopsPage'))
const CreateShopPage = lazy(() => import('./pages/shops/CreateShopPage'))
const EditShopPage = lazy(() => import('./pages/shops/EditShopPage'))

// Páginas de desarrollo/soporte
const TestGroupsPage = lazy(() => import('./pages/TestGroupsPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const NotFoundV4WyraPage = lazy(() => import('./pages/NotFoundV4WyraPage'))
const DonatePage = lazy(() => import('./pages/donations/DonatePage'))
const RedeemCodePage = lazy(() => import('./pages/RedeemCodePage'))
const RestrictedAccessPage = lazy(() => import('./pages/restricted/RestrictedAccessPage'))

// Componente wrapper para rutas con carga personalizada
const LazyRoute = ({ children, fallback = null, showSkeleton = false, message = null }) => (
  <Suspense fallback={fallback || <PageLoader showSkeleton={showSkeleton} message={message} />}>
    {children}
  </Suspense>
)

// Componente para rutas protegidas con carga
const ProtectedLazyRoute = ({ children, ...props }) => (
  <ProtectedRoute>
    <LazyRoute {...props}>
      {children}
    </LazyRoute>
  </ProtectedRoute>
)

// Componente para rutas de administrador con carga
const AdminLazyRoute = ({ children, ...props }) => (
  <AdminRoute component={() => children} {...props}>
    <LazyRoute {...props}>
      {children}
    </LazyRoute>
  </AdminRoute>
)

// Componente para rutas de moderador con carga
const ModeratorLazyRoute = ({ children, ...props }) => (
  <ModeratorRoute component={() => children} {...props}>
    <LazyRoute {...props}>
      {children}
    </LazyRoute>
  </ModeratorRoute>
)

function App() {
  // Pre-cargar recursos críticos al iniciar
  React.useEffect(() => {
    // Pre-cargar fuentes comunes
    if ('fonts' in document) {
      document.fonts.load('400 1em Inter')
      document.fonts.load('600 1em Inter')
      document.fonts.load('700 1em Inter')
    }

    // Iniciar limpieza automática de chats expirados
    console.log('🗓️ Iniciando limpieza automática de chats (cada hora)')
    scheduleCleanup()

    // Limpiar todos los votos del localStorage
    const voteKeys = [
      'ne-global-counts',
      'ne-user-choices',
      'nb-global-counts',
      'nb-user-choices',
      'voteData',
      'userVotes',
      'eventVotes',
      'groupVotes'
    ]

    let clearedCount = 0
    voteKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key)
        clearedCount++
      }
    })

    if (clearedCount > 0) {
      console.log(`🧹 Limpiados ${clearedCount} registros de votos del localStorage`)
    }
  }, [])

  return (
    <ErrorBoundary>
      <SimpleAuthProvider>
        <AnonymousUserProvider>
          <Router>
            <Routes>
              {/* Ruta pública principal - carga prioritaria */}
              <Route
                path="/"
                element={
                  <LazyRoute
                    fallback={<PageLoader message="Cargando Wyra..." showSkeleton />}
                  >
                    <HomePage />
                  </LazyRoute>
                }
              />

              {/* Rutas de autenticación */}
              <Route
                path="/auth/login"
                element={
                  <LazyRoute fallback={<AuthLoader />}>
                    <NeoPixelLoginPage />
                  </LazyRoute>
                }
              />

              <Route
                path="/auth/register"
                element={
                  <LazyRoute fallback={<AuthLoader />}>
                    <NeoPixelRegisterPage />
                  </LazyRoute>
                }
              />

              {/* Ruta de acceso restringido (para usuarios baneados/suspendidos) */}
              <Route
                path="/restricted"
                element={
                  <LazyRoute message="Cargando...">
                    <RestrictedAccessPage />
                  </LazyRoute>
                }
              />

              {/* Rutas de comunidad (públicas) */}
              <Route
                path="/comunidad"
                element={
                  <LazyRoute
                    message="Cargando comunidad..."
                    showSkeleton
                  >
                    <Layout><ComunidadPage /></Layout>
                  </LazyRoute>
                }
              >
                {/* Ruta anidada por defecto - muestra eventos */}
                <Route index element={<ComunidadEventosPage />} />
                {/* Sub-rutas de comunidad */}
                <Route path="eventos" element={<ComunidadEventosPage />} />
                <Route path="grupos" element={<ComunidadGruposPage />} />
                <Route path="chat" element={<ComunidadChatPage />} />
                <Route path="spots" element={<ComunidadSpotsPage />} />
              </Route>

              {/* Ruta dedicada de eventos (pública) - alias directo */}
              <Route
                path="/eventos"
                element={
                  <LazyRoute
                    message="Cargando eventos..."
                    showSkeleton
                  >
                    <Layout><ComunidadEventosPage /></Layout>
                  </LazyRoute>
                }
              />


              {/* Ruta de academia (pública) */}
              <Route
                path="/academia"
                element={
                  <LazyRoute message="Cargando academias..." showSkeleton>
                    <Layout><AcademiesPage /></Layout>
                  </LazyRoute>
                }
              />

              {/* Ruta de academia con estilo Neo-Memphis (pública) */}
              <Route
                path="/neo-academia"
                element={
                  <LazyRoute message="Cargando academias..." showSkeleton>
                    <NeoAcademiesPage />
                  </LazyRoute>
                }
              />

              <Route
                path="/academias"
                element={<Navigate to="/academia" replace />}
              />

              <Route
                path="/academies"
                element={<Navigate to="/academia" replace />}
              />

              {/* Ruta de donaciones (pública) */}
              <Route
                path="/donate"
                element={
                  <LazyRoute message="Cargando página de donaciones...">
                    <Layout><DonatePage /></Layout>
                  </LazyRoute>
                }
              />

              {/* Ruta de canje de códigos (pública) */}
              <Route
                path="/redeem"
                element={
                  <LazyRoute message="Cargando página de canje...">
                    <Layout><RedeemCodePage /></Layout>
                  </LazyRoute>
                }
              />

              <Route
                path="/academies/:id"
                element={
                  <ProtectedLazyRoute message="Cargando detalles de la academia...">
                    <AcademyDetailPage />
                  </ProtectedLazyRoute>
                }
              />

              {/* Legal */}
              <Route
                path="/legal/terms"
                element={
                  <LazyRoute message="Cargando términos...">
                    <Layout><TermsPage /></Layout>
                  </LazyRoute>
                }
              />
              <Route
                path="/legal/privacy"
                element={
                  <LazyRoute message="Cargando privacidad...">
                    <Layout><PrivacyPage /></Layout>
                  </LazyRoute>
                }
              />

              {/* Contacto */}
              <Route
                path="/contacto"
                element={
                  <LazyRoute message="Cargando contacto...">
                    <ContactPage />
                  </LazyRoute>
                }
              />

              {/* Ruta de tiendas (pública) */}
              <Route
                path="/shops"
                element={
                  <LazyRoute message="Buscando tiendas..." showSkeleton>
                    <Layout><ShopsPage /></Layout>
                  </LazyRoute>
                }
              />

              {/* Rutas de creación y edición de tiendas (protegidas) */}
              <Route
                path="/shops/create"
                element={
                  <ProtectedLazyRoute message="Preparando formulario...">
                    <Layout><CreateShopPage /></Layout>
                  </ProtectedLazyRoute>
                }
              />

              {/* Rutas protegidas - agrupadas por funcionalidad */}

              {/* Grupo de rutas de Grupos */}
              <Route
                path="/groups/popular"
                element={
                  <ProtectedLazyRoute message="Cargando grupos populares...">
                    <Layout><GroupsPage /></Layout>
                  </ProtectedLazyRoute>
                }
              />
              <Route
                path="/groups/:groupId"
                element={
                  <ProtectedLazyRoute message="Cargando detalles del grupo...">
                    <GroupDetailPage />
                  </ProtectedLazyRoute>
                }
              />
              <Route
                path="/groups/:groupId/manage"
                element={
                  <ProtectedLazyRoute message="Cargando panel de gestión...">
                    <ManageGroupPage />
                  </ProtectedLazyRoute>
                }
              />
              <Route
                path="/groups/:groupId/edit"
                element={
                  <ProtectedLazyRoute message="Cargando editor de grupos...">
                    <Layout><EditGroupPageWrapper /></Layout>
                  </ProtectedLazyRoute>
                }
              />

              {/* Ruta pública para grupos predefinidos del JSON */}
              <Route
                path="/predefined-groups"
                element={
                  <LazyRoute message="Cargando grupos predefinidos...">
                    <Layout><PredefinedGroupsPage /></Layout>
                  </LazyRoute>
                }
              />

              {/* Ruta pública para grupos con estilo neobrutalista */}
              <Route
                path="/neo-groups"
                element={
                  <LazyRoute message="Cargando directorio de grupos...">
                    <NeoGroupsPage />
                  </LazyRoute>
                }
              />

              {/* Grupo de rutas Sociales */}
              {/* Ruta /chat eliminada - ahora se usa /comunidad/chat */}
              <Route
                path="/profile"
                element={
                  <ProtectedLazyRoute message="Cargando perfil...">
                    <Layout><SimpleProfilePage /></Layout>
                  </ProtectedLazyRoute>
                }
              />

              {/* Grupo de rutas de Configuración */}
              <Route
                path="/settings/notifications"
                element={
                  <ProtectedLazyRoute message="Cargando configuración...">
                    <NotificationSettingsPage />
                  </ProtectedLazyRoute>
                }
              />

              {/* Rutas de Administración */}
              <Route
                path="/admin"
                element={
                  <AdminLazyRoute message="Accediendo al panel...">
                    <Layout><AdminPage /></Layout>
                  </AdminLazyRoute>
                }
              />
              <Route
                path="/admin/moderation"
                element={
                  <AdminLazyRoute message="Accediendo al panel de moderación...">
                    <Layout><ModerationPage /></Layout>
                  </AdminLazyRoute>
                }
              />
              {/* Ruta temporal para setup de admin */}
              <Route
                path="/admin/setup"
                element={
                  <AdminLazyRoute message="Accediendo al panel de configuración...">
                    <TempAdminPage />
                  </AdminLazyRoute>
                }
              />
              {/* Ruta de utilidad para eliminar eventos */}
              <Route
                path="/admin/util/delete-events"
                element={
                  <AdminLazyRoute message="Accediendo a la utilidad de eliminación...">
                    <DeleteAllEventsPage />
                  </AdminLazyRoute>
                }
              />

              {/* Ruta de mantenimiento del sistema */}
              <Route
                path="/mantenimiento"
                element={
                  <AdminLazyRoute message="Accediendo a mantenimiento...">
                    <MaintenancePage />
                  </AdminLazyRoute>
                }
              />

              {/* Ruta de desarrollo/testing */}
              <Route
                path="/test-groups"
                element={
                  <ProtectedLazyRoute message="Cargando modo prueba...">
                    <Layout><TestGroupsPage /></Layout>
                  </ProtectedLazyRoute>
                }
              />

              {/* Redirección para rutas no encontradas */}
              <Route
                path="/404"
                element={
                  <LazyRoute message="Página no encontrada">
                    <Layout><NotFoundV4WyraPage /></Layout>
                  </LazyRoute>
                }
              />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </Router>
        </AnonymousUserProvider>
      </SimpleAuthProvider>
    </ErrorBoundary>
  )
}

export default App